/**
 * OVERLAY MOTION — the props that make a scrim and a panel arrive from
 * somewhere and leave the way they came.
 *
 * Usage with an existing styled scrim/card pair:
 *
 *   const Scrim = m.div`…`;            // from ./styled
 *   const Card  = m.div`…`;
 *
 *   <Presence>
 *     {open && (
 *       <ModalPortal key="dialog">
 *         <Scrim {...scrimMotion} onClick={close}>
 *           <Card {...useOverlayMotion('modal')} role="dialog">…</Card>
 *         </Scrim>
 *       </ModalPortal>
 *     )}
 *   </Presence>
 *
 * `kind` names the spatial origin:
 *   modal         centred, grows from 96% — becomes `sheet` on a phone
 *   sheet         rises from the bottom edge
 *   drawer-right  slides in from the right edge
 *   drawer-left   slides in from the left edge
 *   popover       grows from its anchor (set transform-origin at the site)
 *   takeover      full-bleed, rises with presence
 *   panel         a floating corner panel
 */
import { useEffect, useState } from 'react';
import type { Variants } from 'framer-motion';
import * as preset from './presets';

export type OverlayKind =
  | 'modal'
  | 'sheet'
  | 'drawer-right'
  | 'drawer-left'
  | 'popover'
  | 'takeover'
  | 'panel';

const VARIANTS: Record<OverlayKind, Variants> = {
  modal: preset.modal,
  sheet: preset.sheet,
  'drawer-right': preset.drawerRight,
  'drawer-left': preset.drawerLeft,
  popover: preset.popover,
  takeover: preset.takeover,
  panel: preset.panel,
};

export interface OverlayMotionProps {
  variants: Variants;
  initial: 'hidden';
  animate: 'visible';
  exit: 'hidden';
}

/** Spread onto a motion scrim. */
export const scrimMotion: OverlayMotionProps = {
  variants: preset.scrim,
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
};

/** Spread onto a motion panel. Static — no phone switch. */
export const overlayMotion = (kind: OverlayKind): OverlayMotionProps => ({
  variants: VARIANTS[kind],
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
});

/* The design system turns every centred modal into a bottom sheet under `sm`
   (480px). The motion follows the layout: below that width a `modal` rises
   from the edge it is pinned to instead of growing from the centre. */
const PHONE_QUERY = '(max-width: 479px)';

const readPhone = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(PHONE_QUERY).matches;

export const useIsPhone = (): boolean => {
  const [phone, setPhone] = useState(readPhone);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = () => setPhone(mql.matches);

    onChange();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);

      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);

    return () => mql.removeListener(onChange);
  }, []);

  return phone;
};

/** Spread onto a motion panel. A `modal` becomes a `sheet` on a phone. */
export const useOverlayMotion = (kind: OverlayKind): OverlayMotionProps => {
  const phone = useIsPhone();
  const effective: OverlayKind = kind === 'modal' && phone ? 'sheet' : kind;

  return overlayMotion(effective);
};
